import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { toAppError, type AppError } from '../lib/errors'
import type { Row } from '../lib/db-types'

/**
 * Profile row shape. Narrows `preferences` from the generated `Json` type
 * (which is the wide `string | number | object | ...` union) to a plain
 * key/value bag — that's what `usePreferences` actually reads/writes.
 */
export type Profile = Omit<Row<'profiles'>, 'preferences'> & {
  preferences: Record<string, unknown>
}

export const profileKey = (userId: string | undefined): QueryKey =>
  ['profile', userId ?? 'anon']

/**
 * Reads the authenticated user's `profiles` row.
 *
 * Replaces the `profile` surface that used to live on `AuthProvider`. Cache
 * invalidation on profile changes is driven by the shared `RealtimeBridge`,
 * not a per-hook channel.
 *
 * Return shape mirrors the old `useAuth()` profile fields so components can
 * swap the import without changing their usage.
 */
export function useProfile() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = profileKey(user?.id)

  const query = useQuery<Profile | null>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name, avatar_url, linkedin_profile_urn, preferences, current_team_id, created_at, updated_at')
        .eq('id', user!.id)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as Profile | null
    },
  })

  /**
   * In-place preferences patch — used by `usePreferences` to seed the cache
   * with the committed RPC result and avoid a round-trip refetch.
   */
  const setProfilePreferences = (preferences: Record<string, unknown>) => {
    qc.setQueryData<Profile | null>(key, (prev) => (prev ? { ...prev, preferences } : prev))
  }

  return {
    profile: query.data ?? null,
    profileLoading: !!user && query.isPending,
    profileError: query.error ? toAppError(query.error) : null as AppError | null,
    refreshProfile: () => qc.invalidateQueries({ queryKey: key }),
    /** @internal — only `usePreferences` should call this. */
    _setProfilePreferences: setProfilePreferences,
  }
}

/**
 * Mutate `profiles.display_name`. Kept here so AccountSettings isn't the one
 * place in the app writing to the `profiles` table directly.
 */
export function useUpdateDisplayName() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['profile', 'updateDisplayName'],
    mutationFn: async (displayName: string) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName, updated_at: new Date().toISOString() })
        .eq('id', user.id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileKey(user?.id) })
    },
  })
}
